import React, { useState } from "react";
import api from "../../services/api";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import FunctionButton from "../../components/common/FunctionButton";
import InputWrapDiv from "../../components/common/InputWrapDiv";
import TopBoxDiv from "../../components/common/TopBoxDiv";
import ContentInput from "../../components/common/ContentInput";
import { useTranslation } from "react-i18next";

const TYPE_OPTIONS = ["DIST", "PORT", "DEAL"];

const DealerCodeManagementEditModal = ({
  editTarget,
  canEdit,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();

  const isEdit = editTarget !== null && editTarget !== undefined;

  const [type, setType] = useState(editTarget?.type || "");
  const [code, setCode] = useState(editTarget?.code || "");
  const [cdDesc, setCdDesc] = useState(editTarget?.cdDesc || "");

  // CODE 중복확인 상태 (수정 시 기존 값은 확인된 것으로 간주)
  const [codeChecked, setCodeChecked] = useState(isEdit);
  const [showAvailMsg, setShowAvailMsg] = useState(false);

  const [errors, setErrors] = useState({
    validType: "",
    validCode: "",
    validCdDesc: "",
  });

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setErrors((prev) => ({ ...prev, validType: "" }));
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
    // 값이 바뀌면 이전 중복확인 결과 무효화
    setCodeChecked(false);
    setShowAvailMsg(false);
    setErrors((prev) => ({ ...prev, validCode: "" }));
  };

  const handleCheck = () => {
    if (!code.trim()) {
      setShowAvailMsg(false);
      setErrors((prev) => ({ ...prev, validCode: "Code is required." }));
      return;
    }

    api
      .post("/manage/dealercode/check", {
        code: code.trim(),
        ...(isEdit ? { id: editTarget.id } : {}),
      })
      .then((response) => {
        if (response.data === 0) {
          setCodeChecked(true);
          setShowAvailMsg(true);
          setErrors((prev) => ({ ...prev, validCode: "" }));
        } else {
          setCodeChecked(false);
          setShowAvailMsg(false);
          setErrors((prev) => ({ ...prev, validCode: t("L0124") }));
        }
      })
      .catch(() => {});
  };

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!type) {
      tempErrors.validType = "Type is required.";
      isValid = false;
    }

    if (!code.trim()) {
      tempErrors.validCode = "Code is required.";
      isValid = false;
    } else if (!codeChecked) {
      // 중복확인 미완료
      tempErrors.validCode = t("L1109");
      isValid = false;
    }

    if (!cdDesc.trim()) {
      tempErrors.validCdDesc = "Name is required.";
      isValid = false;
    }

    setErrors(tempErrors);
    if (!isValid) {
      setShowAvailMsg(false);
    }
    return isValid;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      type: type,
      code: code.trim(),
      cdDesc: cdDesc.trim(),
    };

    const request = isEdit
      ? api.post("/manage/dealercode/update", {
          id: editTarget.id,
          ...payload,
        })
      : api.post("/manage/dealercode/add", payload);

    request
      .then(() => {
        onSaved();
      })
      .catch((error) => {
        if (error?.response?.data?.result === 1) {
          setShowAvailMsg(false);
          setCodeChecked(false);
          setErrors({ validCode: t("L0124") });
        }
      });
  };

  return (
    <div>
      <InputWrapDiv>
        <TopBoxDiv value={t("MENU:L1104")}></TopBoxDiv>

        <table>
          <tbody>
            <tr>
              <th>
                {t("L1105")} *
                {errors.validType && (
                  <>
                    <br />
                    <span style={{ color: "red", fontSize: "10px" }}>
                      {errors.validType}
                    </span>
                  </>
                )}
              </th>
              <td>
                <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                  {TYPE_OPTIONS.map((opt) => (
                    <span key={opt} style={{ display: "inline-flex", alignItems: "center" }}>
                      <input
                        type="radio"
                        id={`dealer-type-${opt}`}
                        name="dealer-type"
                        value={opt}
                        checked={type === opt}
                        onChange={handleTypeChange}
                        disabled={!canEdit}
                      />
                      <label
                        htmlFor={`dealer-type-${opt}`}
                        style={{ paddingLeft: "22px", lineHeight: "17px" }}
                      >
                        {opt}
                      </label>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
            <tr>
              <th>
                {t("L1107")} *
                {errors.validCode && (
                  <>
                    <br />
                    <span style={{ color: "red", fontSize: "10px" }}>
                      {errors.validCode}
                    </span>
                  </>
                )}
                {!errors.validCode && showAvailMsg && (
                  <>
                    <br />
                    <span style={{ color: "#0067a3", fontSize: "10px" }}>
                      {t("L1108")}
                    </span>
                  </>
                )}
              </th>
              <td>
                <div style={{ display: "flex", gap: "4px" }}>
                  <ContentInput
                    value={code}
                    maxLength={16}
                    width="70%"
                    onChange={handleCodeChange}
                    disabled={!canEdit}
                  ></ContentInput>
                  <FunctionButton
                    className="small"
                    value={t("L0359")}
                    onClick={handleCheck}
                  ></FunctionButton>
                </div>
              </td>
            </tr>
            <tr>
              <th>
                {t("L1106")} *
                {errors.validCdDesc && (
                  <>
                    <br />
                    <span style={{ color: "red", fontSize: "10px" }}>
                      {errors.validCdDesc}
                    </span>
                  </>
                )}
              </th>
              <td>
                <ContentInput
                  value={cdDesc}
                  maxLength={100}
                  width="70%"
                  onChange={(e) => setCdDesc(e.target.value)}
                  disabled={!canEdit}
                ></ContentInput>
              </td>
            </tr>
          </tbody>
        </table>
      </InputWrapDiv>

      <ButtonWrapDiv className="btn_bottom" style={{ margin: "16px 0" }}>
        {canEdit && (
          <FunctionButton
            className="big"
            value={t("L0659")}
            onClick={handleSubmit}
          ></FunctionButton>
        )}
        <FunctionButton
          className="big bg_gray"
          value={t("L0005")}
          onClick={onClose}
        ></FunctionButton>
      </ButtonWrapDiv>
    </div>
  );
};

export default DealerCodeManagementEditModal;
